import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { track } from '@theme/r-analytics';

/**
 * AMP Back in Stock's signup endpoint, reached through the store's app proxy.
 * Their widget calls it as JSONP; without `callback` it answers plain JSON:
 *   { status: 'OK', message }                       on success
 *   { status: 'Error', errors: { email: ['…'] } }   on refusal, already localised
 * Always the shop root — AMP's own widget ignores the locale prefix too.
 */
const ENDPOINT = '/apps/bis/signups/create.json';

/** A stalled app proxy must not leave the button spinning; the shopper can retry. */
const REQUEST_TIMEOUT_MS = 10000;

/**
 * PDP back-in-stock signup (blocks/r-pdp-notify-me.liquid).
 *
 * Mounted inside the drawer body; the row that holds the trigger is the block's
 * outer element, found with closest(). Visibility and the selected variant come
 * from Liquid — on every variant change the new row is read out of the section
 * HTML the variant picker already fetched, so no eligibility rule lives here.
 *
 * @extends {Component}
 */
class RNotifyMe extends Component {
  requiredRefs = ['form', 'email', 'variantId', 'submit', 'error', 'formStep', 'doneStep'];

  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abort;
    this.refs.form.addEventListener('submit', this.#handleSubmit, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    super.disconnectedCallback();
  }

  /** @returns {HTMLElement | null} */
  get #row() {
    return this.closest('[data-r-notify-me-row]');
  }

  /**
   * VariantUpdateEvent bubbles to `document` from every product form on the page,
   * so match the product before reacting (a quick-add modal must not retarget this).
   *
   * @param {Event} event
   */
  #handleVariantUpdate = (event) => {
    const { detail } = /** @type {any} */ (event);
    if (!detail || String(detail.data?.productId ?? '') !== this.dataset.productId) return;

    const row = this.#row;
    if (!row) return;

    const blockId = row.dataset.rNotifyMeRow;
    /** @type {HTMLElement | null | undefined} */
    const next = detail.data.html?.querySelector(`[data-r-notify-me-row="${CSS.escape(blockId ?? '')}"]`);

    // No row in the new HTML means Liquid decided not to render one — including
    // an unavailable option combination (`resource` null), which has no variant.
    if (!next || !detail.resource) {
      row.hidden = true;
      return;
    }

    row.hidden = next.hidden;
    row.dataset.variantId = next.dataset.variantId ?? '';
    this.refs.variantId.value = next.dataset.variantId ?? '';

    const title = next.dataset.variantTitle ?? '';
    const { variantLabel } = this.refs;
    if (variantLabel) {
      variantLabel.textContent = title;
      variantLabel.hidden = title === '';
    }

    this.#showError('');
    this.#showStep('form');
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();

    const email = this.refs.email.value.trim();
    const variantId = this.refs.variantId.value;
    if (!email || !variantId) return;

    this.#showError('');
    this.#setBusy(true);

    const acceptsMarketing = Boolean(this.refs.acceptsMarketing?.checked);

    try {
      const response = await fetch(`${ENDPOINT}?${this.#payload(email, variantId, acceptsMarketing)}`, {
        headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
      const data = await response.json();

      if (data?.status === 'OK') {
        track('back_in_stock_signup', {
          product_id: this.dataset.productId,
          variant_id: variantId,
          accepts_marketing: acceptsMarketing,
          status: 'success',
        });
        this.#showStep('done');
        this.refs.doneHeading?.focus();
        return;
      }

      track('back_in_stock_signup', { product_id: this.dataset.productId, variant_id: variantId, status: 'refused' });
      this.#showError(this.#messageFrom(data?.errors));
    } catch (error) {
      console.error('[r-notify-me]', error);
      track('back_in_stock_signup', { product_id: this.dataset.productId, variant_id: variantId, status: 'error' });
      this.#showError(this.dataset.errorGeneric ?? '');
    } finally {
      this.#setBusy(false);
    }
  };

  /**
   * The same fields AMP's widget sends (BIS.Base#_buildCreatePayload), in Rails'
   * bracket notation. `recaptcha_response` is omitted: reCAPTCHA is off for this
   * shop — if it is ever switched on in AMP, signups from here will be refused.
   *
   * @param {string} email
   * @param {string} variantId
   * @param {boolean} acceptsMarketing
   */
  #payload(email, variantId, acceptsMarketing) {
    const params = new URLSearchParams({
      shop: this.dataset.shop ?? '',
      'variant[variant_no]': variantId,
      'notification[channel]': 'email',
      'notification[email]': email,
      'notification[product_no]': this.dataset.productId ?? '',
      'notification[quantity_required]': '1',
      'notification[accepts_marketing]': String(acceptsMarketing),
      'notification[customer_utc_offset]': String(new Date().getTimezoneOffset() * 60),
      'notification[customer_language]': navigator.language || '',
    });
    if (this.dataset.localeId) params.set('notification[shop_locale_id]', this.dataset.localeId);
    return params.toString();
  }

  /** @param {Record<string, string[] | string> | undefined} errors */
  #messageFrom(errors) {
    const messages = Object.values(errors ?? {}).flat().filter(Boolean);
    return messages.length > 0 ? messages.join(' ') : this.dataset.errorGeneric ?? '';
  }

  /** @param {string} message - Empty to clear. */
  #showError(message) {
    const { error, email } = this.refs;
    error.textContent = message;
    error.hidden = message === '';
    if (message) email.setAttribute('aria-invalid', 'true');
    else email.removeAttribute('aria-invalid');
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    const { submit } = this.refs;
    submit.disabled = busy;
    submit.setAttribute('aria-busy', String(busy));
  }

  /** @param {'form' | 'done'} step */
  #showStep(step) {
    this.refs.formStep.hidden = step !== 'form';
    this.refs.doneStep.hidden = step !== 'done';
  }
}

if (!customElements.get('r-notify-me')) {
  customElements.define('r-notify-me', RNotifyMe);
}
